import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronRight, Flame, Phone, Search } from 'lucide-react';
import clsx from 'clsx';

import StatusPill from '@/components/StatusPill';
import ClientsTabs from '@/components/ClientsTabs';
import { CallMarkButtons, LastCall, useCallMarker } from '@/components/CallMarks';
import { apiGet } from '@/lib/api';
import {
  CALL_BUCKETS,
  CALL_PERIODS,
  callBucket,
  callPeriodStart,
  parseCallBucket,
  parseCallPeriod,
  type CallBucket,
  type CallSummary,
} from '@/lib/calls';
import { callTarget, type CallTarget } from '@/lib/phone';
import type { BackState } from '@/lib/back-nav';
import {
  SORTS,
  establishmentsCacheScope,
  isExpiring,
  parseSort,
  readEstablishmentsCache,
  writeEstablishmentsCache,
  type EstablishmentRow,
} from '@/lib/establishments';
import { formatDate, formatKop } from '@/lib/format';
import { daysLeftLabel, statusTone, type SubscriptionInfo } from '@/lib/subscription';
import { useAuthStore, useIsHeadManager } from '@/store/auth.store';

/** Фильтр главного по менеджеру: все / свои / без менеджера / конкретный менеджер. */
type ManagerFilter = 'all' | 'mine' | 'none' | (string & {});

export default function Establishments() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const managerId = useAuthStore((s) => s.manager?.id ?? '');
  const role = useAuthStore((s) => s.manager?.role);
  // Руководитель видит все точки платформы так же, как главный.
  const isHead = useIsHeadManager();
  const scope = establishmentsCacheScope(managerId, role);

  // Сортировка и фильтры живут в адресе: «назад» из карточки возвращает тот же вид.
  const sort = parseSort(params.get('sort'));
  const hotOnly = params.get('hot') === '1';
  const managerFilter: ManagerFilter = isHead ? params.get('m') || 'all' : 'all';
  // Прозвон: группа и период — тоже в адресе, «назад» из карточки их сохраняет.
  const callFilter = parseCallBucket(params.get('call'));
  const callPeriod = parseCallPeriod(params.get('cp'));
  const [query, setQuery] = useState(() => params.get('q') ?? '');

  const [items, setItems] = useState<EstablishmentRow[]>(
    () => readEstablishmentsCache(scope, sort) ?? [],
  );
  const [loaded, setLoaded] = useState(() => readEstablishmentsCache(scope, sort) !== null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!managerId) return;
    let cancelled = false;
    const cached = readEstablishmentsCache(scope, sort);
    if (cached) {
      setItems(cached);
      setLoaded(true);
    }
    setError(null);
    // Кеш показан сразу, сервер его тихо освежает: ошибка фона — не повод для тоста.
    void apiGet<EstablishmentRow[]>(`/establishments?sort=${sort}`, { silent: !!cached })
      .then((rows) => {
        if (cancelled) return;
        writeEstablishmentsCache(scope, sort, rows);
        setItems(rows);
        setLoaded(true);
      })
      .catch(() => {
        if (!cancelled && !cached) setError('Не удалось загрузить заведения');
      });
    return () => {
      cancelled = true;
    };
  }, [managerId, scope, sort]);

  const updateParam = (key: string, value: string | null) => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );
  };

  const needle = query.trim().toLowerCase();
  const base = useMemo(
    () =>
      items.filter((r) => {
        if (hotOnly && !isExpiring(r)) return false;
        if (managerFilter === 'mine' && !r.mine) return false;
        if (managerFilter === 'none' && r.referrer) return false;
        if (
          managerFilter !== 'all' &&
          managerFilter !== 'mine' &&
          managerFilter !== 'none' &&
          r.referrer?.id !== managerFilter
        ) {
          return false;
        }
        if (needle && !matchesQuery(r, needle)) return false;
        return true;
      }),
    [items, hotOnly, managerFilter, needle],
  );

  // Группы прозвона считаются по остальным фильтрам: «Не звонили · 12» — из того, что сейчас в списке.
  const since = callPeriodStart(callPeriod);
  const callCounts = useMemo(() => {
    const counts: Record<CallBucket, number> = { answered: 0, no_answer: 0, none: 0 };
    for (const r of base) counts[callBucket(r.call ?? null, since)] += 1;
    return counts;
  }, [base, since]);
  const visible = useMemo(
    () => (callFilter ? base.filter((r) => callBucket(r.call ?? null, since) === callFilter) : base),
    [base, callFilter, since],
  );

  // Отметка звонка сразу двигает строку в её группу; кеш списка правит сам маркер.
  const { mark, busyId } = useCallMarker((establishmentId: string, call: CallSummary | null) => {
    setItems((rows) => rows.map((r) => (r.id === establishmentId ? { ...r, call } : r)));
  });

  // Менеджеры, у которых есть точки в списке (кроме себя — для этого «Мои»).
  const managerOptions = useMemo(() => {
    if (!isHead) return [];
    const byId = new Map<string, { id: string; name: string; count: number }>();
    for (const r of items) {
      if (!r.referrer || r.mine) continue;
      const cur = byId.get(r.referrer.id) ?? { id: r.referrer.id, name: r.referrer.name, count: 0 };
      cur.count += 1;
      byId.set(r.referrer.id, cur);
    }
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name, 'ru'));
  }, [items, isHead]);

  const expiring = items.filter(isExpiring).length;
  const filtered = visible.length !== items.length;
  const backState: BackState = { backTo: location.pathname + location.search, backSteps: 1 };
  const openRow = (id: string) => navigate(`/establishments/${id}`, { state: backState });

  return (
    <div className={clsx('space-y-4', isHead ? 'max-w-6xl' : 'max-w-5xl')}>
      <ClientsTabs />
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold">
            {isHead ? 'Все заведения' : 'Заведения'}
          </h1>
          <p className="mt-1 text-sm text-ink/55">
            {items.length
              ? filtered
                ? `${visible.length} из ${items.length}`
                : isHead
                  ? `${items.length} на платформе`
                  : `${items.length} подключено`
              : isHead
                ? 'Заведений пока нет'
                : 'Подключённых заведений пока нет'}
            {expiring > 0 ? ` · ${expiring} с истекающим сроком` : ''}
          </p>
        </div>
        <select
          className="input sm:w-64"
          value={sort}
          onChange={(e) => updateParam('sort', e.target.value === 'attached_desc' ? null : e.target.value)}
          aria-label="Сортировка"
        >
          {SORTS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
      </div>

      {items.length > 0 ? (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink/35" />
            <input
              className="input !pl-9"
              type="search"
              enterKeyHint="search"
              placeholder="Название, адрес, владелец или телефон"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                updateParam('q', e.target.value.trim() || null);
              }}
              aria-label="Поиск заведения"
            />
          </div>
          {isHead ? (
            <select
              className="input sm:w-56"
              value={managerFilter}
              onChange={(e) => updateParam('m', e.target.value === 'all' ? null : e.target.value)}
              aria-label="Менеджер"
            >
              <option value="all">Все менеджеры</option>
              <option value="mine">Мои</option>
              <option value="none">Без менеджера</option>
              {managerOptions.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} · {m.count}
                </option>
              ))}
            </select>
          ) : null}
          <button
            type="button"
            onClick={() => updateParam('hot', hotOnly ? null : '1')}
            className={clsx(
              'btn shrink-0 border',
              hotOnly ? 'border-rose-200 bg-rose-50 text-rose-700' : 'border-line bg-white text-ink/70 hover:bg-paper',
            )}
            aria-pressed={hotOnly}
          >
            <Flame size={16} /> Горят{expiring > 0 ? ` · ${expiring}` : ''}
          </button>
        </div>
      ) : null}

      {items.length > 0 ? (
        <div className="space-y-2 sm:flex sm:items-center sm:gap-3 sm:space-y-0" role="group" aria-label="Прозвон">
          <div className="flex items-center justify-between gap-2 sm:justify-start">
            <span className="text-sm font-medium text-ink/70">Прозвон</span>
            <select
              className="input !w-auto !py-1.5 !pl-2.5 !pr-8 !text-sm"
              value={callPeriod}
              onChange={(e) => updateParam('cp', e.target.value === 'today' ? null : e.target.value)}
              aria-label="Период прозвона"
            >
              {CALL_PERIODS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
          {/* Группы — плитки с числом: нажали — список показывает только их, ещё раз — все. */}
          <div className="grid grid-cols-3 gap-2 sm:flex">
            {CALL_BUCKETS.map((b) => {
              const active = callFilter === b.value;
              return (
                <button
                  key={b.value}
                  type="button"
                  onClick={() => updateParam('call', active ? null : b.value)}
                  aria-pressed={active}
                  className={clsx(
                    'flex min-h-[44px] flex-col items-start justify-center rounded-lg border px-2.5 py-1.5 text-left sm:min-w-[132px] sm:flex-row sm:items-center sm:justify-between sm:gap-3 sm:px-3',
                    active ? 'border-orange bg-orange/10' : 'border-line bg-white hover:bg-paper',
                  )}
                >
                  <span className={clsx('text-xs sm:text-sm', active ? 'font-medium text-ink' : 'text-ink/60')}>
                    {b.label}
                  </span>
                  <span className={clsx('text-base font-semibold tabular-nums leading-tight', !active && callCounts[b.value] === 0 && 'text-ink/35')}>
                    {callCounts[b.value]}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {!loaded && !items.length && !error ? <div className="text-ink/50 text-sm">Загрузка…</div> : null}
      {error ? <div className="card p-4 text-sm text-ink/70">{error}</div> : null}

      {loaded && !error && !items.length ? (
        <div className="card p-6 text-sm text-ink/55">
          {isHead
            ? 'На платформе пока нет заведений.'
            : 'Пока нет подключённых заведений. Приводите клиентов по своей ссылке из раздела «Обзор».'}
        </div>
      ) : null}

      {items.length > 0 && !visible.length ? (
        <div className="card p-6 text-sm text-ink/55">
          {callFilter === 'none' && base.length
            ? 'Все заведения из списка уже прозвонили за этот период.'
            : 'Под фильтр ничего не попало.'}
        </div>
      ) : null}

      {visible.length > 0 && (
        <>
          {/* Mobile */}
          <div className="space-y-3 md:hidden">
            {visible.map((r) => {
              const call = callTarget(r);
              return (
                <div key={r.id} className="card overflow-hidden">
                  <Link
                    to={`/establishments/${r.id}`}
                    state={backState}
                    className="block p-4 space-y-3 active:bg-paper"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-medium">{r.name}</div>
                        <div className="mt-0.5 text-xs text-ink/45">{r.city ?? '—'}</div>
                      </div>
                      <StatusPill sub={r.subscription} />
                    </div>

                    <div className="flex items-baseline justify-between gap-3 border-t border-line/60 pt-3">
                      <span className="text-xs text-ink/45">Осталось</span>
                      <DaysLeft sub={r.subscription} />
                    </div>

                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <div className="min-w-0">
                        <div className="text-[11px] text-ink/45">Оборот 30 дн.</div>
                        <Revenue kopecks={r.revenue30dKopecks} />
                      </div>
                      <div className="min-w-0">
                        <div className="text-[11px] text-ink/45">Подписка</div>
                        <div className="truncate font-medium tabular-nums">{formatKop(r.paymentsTotalKopecks)}</div>
                        <div className="text-[11px] text-ink/40">{r.paymentsCount} плат.</div>
                      </div>
                      {isHead && !r.mine ? (
                        <div className="min-w-0">
                          <div className="text-[11px] text-ink/45">Менеджер</div>
                          <div className="truncate font-medium">{r.referrer?.name ?? 'без менеджера'}</div>
                        </div>
                      ) : (
                        <div className="min-w-0">
                          <div className="text-[11px] text-ink/45">Моя комиссия</div>
                          <div className="truncate font-medium tabular-nums">{formatKop(r.commissionTotalKopecks)}</div>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-between gap-2 text-xs text-ink/50">
                      <span className="min-w-0">
                        {r.subscription?.plan ? `Тариф ${r.subscription.plan} · ` : ''}
                        {r.networkSize > 1 ? `сеть из ${r.networkSize} · ` : ''}
                        оплата {formatDate(r.lastPaymentAt)}
                      </span>
                      <ChevronRight size={16} className="shrink-0 text-ink/30" />
                    </div>
                  </Link>
                  {call ? <CallStrip call={call} /> : null}
                  {/* Итог звонка — сразу под звонком: позвонил, вернулся, отметил. */}
                  <div className="space-y-2 border-t border-line/60 px-4 pb-3 pt-2.5">
                    <LastCall call={r.call} since={since} showWho={isHead} className="max-w-full" />
                    <CallMarkButtons
                      className="grid grid-cols-2"
                      busy={busyId === r.id}
                      onMark={(res) => void mark(r.id, res)}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop */}
          <div className="card overflow-x-auto hidden md:block">
            <table className={clsx('w-full text-sm', isHead ? 'min-w-[1080px]' : 'min-w-[960px]')}>
              <thead>
                <tr className="text-left text-ink/50 border-b border-line">
                  <th className="px-4 py-3">Заведение</th>
                  {isHead ? <th className="px-4 py-3">Менеджер</th> : null}
                  <th className="px-4 py-3">Подписка</th>
                  <th className="px-4 py-3">Осталось</th>
                  <th className="whitespace-nowrap px-4 py-3 text-right" title="Выручка заведения через кассу за 30 дней">
                    Оборот 30 дн.
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 text-right">Оплаты подписки</th>
                  <th className="px-4 py-3 text-right">Моя комиссия</th>
                  <th className="px-4 py-3">Звонок</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => {
                  const call = callTarget(r);
                  return (
                  <tr
                    key={r.id}
                    className="cursor-pointer border-b border-line/60 last:border-0 hover:bg-paper"
                    onClick={() => openRow(r.id)}
                  >
                    <td className="px-4 py-3">
                      <Link
                        to={`/establishments/${r.id}`}
                        state={backState}
                        className="font-medium hover:text-orange"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {r.name}
                      </Link>
                      <div className="text-xs text-ink/45">
                        {r.city ?? '—'} · с {formatDate(isHead ? r.createdAt : r.attachedAt)}
                      </div>
                    </td>
                    {isHead ? (
                      <td className="px-4 py-3">
                        {r.mine ? (
                          <span className="font-medium">Вы</span>
                        ) : r.referrer ? (
                          <>
                            <div className="font-medium">{r.referrer.name}</div>
                            <div className="text-xs text-ink/45">{r.referrer.code}</div>
                          </>
                        ) : (
                          <span className="whitespace-nowrap text-ink/40">без менеджера</span>
                        )}
                      </td>
                    ) : null}
                    <td className="px-4 py-3">
                      <StatusPill sub={r.subscription} />
                      {r.subscription || r.networkSize > 1 ? (
                        <div className="mt-1 text-xs text-ink/45">
                          {r.subscription?.plan ?? ''}
                          {r.networkSize > 1 ? (
                            <span title="У владельца несколько заведений. Подписка у каждого своя, оплачиваются отдельно.">
                              {r.subscription ? ' · ' : ''}сеть из {r.networkSize}
                            </span>
                          ) : null}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3">
                      <DaysLeft sub={r.subscription} />
                      {r.subscription?.accessEndsAt ? (
                        <div className="text-xs text-ink/45">
                          до {formatDate(r.subscription.accessEndsAt)}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Revenue kopecks={r.revenue30dKopecks} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="tabular-nums">{formatKop(r.paymentsTotalKopecks)}</span>
                      <div className="whitespace-nowrap text-xs text-ink/45">
                        {r.paymentsCount} плат.{r.lastPaymentAt ? ` · ${formatDate(r.lastPaymentAt)}` : ''}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-medium">
                      {isHead && !r.mine ? (
                        <span className="text-ink/30">—</span>
                      ) : (
                        formatKop(r.commissionTotalKopecks)
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {call ? (
                        <a
                          href={call.href}
                          onClick={(e) => e.stopPropagation()}
                          title={`Позвонить: ${call.who}`}
                          className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg px-2 py-1 -mx-2 text-emerald-700 hover:bg-emerald-50"
                        >
                          <Phone size={14} className="shrink-0" />
                          <span className="tabular-nums">{call.phone}</span>
                        </a>
                      ) : (
                        <span className="text-ink/30">—</span>
                      )}
                      {call ? <div className="max-w-[180px] truncate text-xs text-ink/45">{call.who}</div> : null}
                      <div className="mt-2 flex items-center gap-2">
                        <CallMarkButtons compact busy={busyId === r.id} onMark={(res) => void mark(r.id, res)} />
                      </div>
                      <LastCall call={r.call} since={since} showWho={isHead} wrap className="mt-1 max-w-[200px]" />
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

/** Оборот кассы: ноль приглушён — точка платит, но не продаёт. */
function Revenue({ kopecks }: { kopecks: number }) {
  return (
    <div className={clsx('truncate tabular-nums', kopecks > 0 ? 'font-medium' : 'text-ink/35')}>
      {formatKop(kopecks)}
    </div>
  );
}

/** Звонок из списка — полоса под карточкой: ссылку в ссылку не вкладываем. */
function CallStrip({ call }: { call: CallTarget }) {
  return (
    <a
      href={call.href}
      className="flex items-center gap-3 border-t border-line/60 px-4 py-2.5 text-sm active:bg-paper"
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
        <Phone size={15} />
      </span>
      <span className="min-w-0 flex-1 truncate">
        <span className="font-medium">Позвонить</span>
        <span className="text-ink/50"> · {call.who}</span>
      </span>
      <span className="shrink-0 text-xs tabular-nums text-ink/55">{call.phone}</span>
    </a>
  );
}

/** Поиск по названию, адресу, владельцу и телефону (цифры, с 8 или +7 в начале). */
function matchesQuery(r: EstablishmentRow, needle: string): boolean {
  if (`${r.name} ${r.city ?? ''} ${r.ownerName ?? ''}`.toLowerCase().includes(needle)) return true;
  const digits = needle.replace(/\D/g, '');
  if (digits.length < 3) return false;
  const local = digits.length > 4 && /^[78]/.test(digits) ? digits.slice(1) : null;
  return [r.ownerPhone, r.phone].some((p) => {
    const d = p?.replace(/\D/g, '') ?? '';
    return d.includes(digits) || (local !== null && d.includes(local));
  });
}

/** Остаток срока: горящее подсвечиваем цветом, чтобы искать глазами не пришлось. */
function DaysLeft({ sub }: { sub: SubscriptionInfo | null }) {
  if (!sub) return <span className="text-ink/40">—</span>;
  const tone = statusTone(sub);
  const cls =
    tone === 'red' ? 'text-rose-600' : tone === 'amber' ? 'text-amber-600' : 'text-ink';
  return <span className={`text-sm font-medium ${cls}`}>{daysLeftLabel(sub)}</span>;
}
